import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Search, MapPin } from "lucide-react";

export default function MemberDirectory() {
  const [members, setMembers] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return members;
    return members.filter((m) =>
      [m.name, m.credentials, m.specialty, m.city].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [members, query]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="directory-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Member directory</div>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl">Arizona clinicians in AzSAM</h1>
          <p className="mt-3 text-[color:var(--azsam-ink-soft)] max-w-2xl">
            Search by name, specialty, or city to connect with colleagues, coordinate care, or find a
            local mentor. Visible only to authenticated members.
          </p>
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--azsam-ink-soft)]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search members…"
            className="pl-9 pr-4 py-3 rounded-lg border border-[color:var(--azsam-stone)] w-64 focus:outline-none focus:border-[color:var(--azsam-clay)]"
            data-testid="directory-search"
          />
        </div>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="directory-grid">
        {filtered.map((m) => (
          <div key={m.id} className="azsam-card p-7" data-testid={`member-${m.id}`}>
            <div className="w-14 h-14 rounded-full bg-[color:var(--azsam-clay)]/10 text-[color:var(--azsam-clay)] font-heading text-xl flex items-center justify-center">
              {m.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?"}
            </div>
            <h3 className="mt-5 font-heading text-xl">{m.name}</h3>
            <div className="text-xs text-[color:var(--azsam-ink-soft)] mt-1">{m.credentials}</div>
            {m.specialty && <div className="mt-3 text-sm text-[color:var(--azsam-ink)]">{m.specialty}</div>}
            {m.city && (
              <div className="mt-3 text-xs text-[color:var(--azsam-ink-soft)] inline-flex items-center gap-1">
                <MapPin size={12} /> {m.city}
              </div>
            )}
            {m.bio && <p className="mt-4 text-sm text-[color:var(--azsam-ink-soft)] leading-relaxed line-clamp-3">{m.bio}</p>}
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-[color:var(--azsam-ink-soft)]" data-testid="directory-empty">No members match your search.</div>
      )}
    </div>
  );
}
